// Purchase orders, stock counts and an item's levels in the office (inventory prototype; gaps #10 and
// #15): the words and the inputs, kept apart from the screens so they are tested. Shapes are those of
// purchaseOrders.get / list, vendors.list and stockCounts.post; who sees which control is the
// permission its action asks for, never a role name (QA-681).

import { validQty } from './model.ts';

/** purchasing.order (purchaseOrders.create, updateLines, send, get, list): owner, manager, warehouse. */
export const ORDER_PERMS = ['purchasing.order'];
/** inventory.manage (purchaseOrders.receive, stockCounts.post, stock.setLevels, stock.setItem). */
export const MANAGE_PERMS = ['inventory.manage'];

export interface Vendor {
  id: string;
  name: string;
  email: string | null;
  active: boolean;
}

export interface PoLine {
  id: string;
  itemId: string;
  description: string;
  quantity: string;
  unitCostCents: number;
  receivedQty: string;
  cancelledQty: string;
  backOrdered: boolean;
  /** Truck-stock parity 4: the line is in this unit ("box"), holding this many single units. */
  purchaseUnit?: string | null;
  unitsPerPurchase?: string;
}

export type PoStatus = 'draft' | 'sending' | 'sent' | 'partially_received' | 'received' | 'cancelled';

export interface Po {
  id: string;
  number: string;
  vendorId: string;
  locationId: string;
  jobId: string | null;
  /** The job's number, when the PO is for one job's parts (F-24). */
  jobNumber: string | null;
  status: PoStatus | string;
  auto: boolean;
  totalCents: number;
  needsOwnerApproval: boolean;
  approvedCents: number | null;
  sentAt: string | null;
  /** The office's own note on the PO. */
  note?: string | null;
  /** Why the last send didn't go (a bounce, no mail provider); null once one does (QA-566). */
  sendFailure?: string | null;
  /** ServiceTitan's PO, loaded as history: read-only while the company mirrors it (BL-088). */
  fromServiceTitan?: boolean;
  /** purchaseOrders.cancel: when and why; for a sent PO, whether its vendor was told. */
  cancelled?: {
    at: string;
    reason: string;
    vendorToldAt: string | null;
    vendorTellFailure: string | null;
  } | null;
  lines: PoLine[];
}

export const PO_STATUS: Record<string, string> = {
  draft: 'Draft',
  sending: 'Sending…',
  sent: 'Sent',
  partially_received: 'Part received',
  received: 'Received',
  cancelled: 'Cancelled',
};

/** The filters over purchaseOrders.list, in the order the office works them. */
export const PO_FILTERS: { status: string | null; label: string }[] = [
  { status: null, label: 'All' },
  { status: 'draft', label: 'Draft' },
  { status: 'sent', label: 'Sent' },
  { status: 'partially_received', label: 'Part received' },
  { status: 'received', label: 'Received' },
  { status: 'cancelled', label: 'Cancelled' },
];

const thousandths = (s: string) => Math.round(Number(s) * 1000);

/** What is still expected on a line: ordered, less received and cancelled. */
export const openQty = (l: Pick<PoLine, 'quantity' | 'receivedQty' | 'cancelledQty'>) =>
  Math.max(0, thousandths(l.quantity) - thousandths(l.receivedQty) - thousandths(l.cancelledQty)) / 1000;

export const editable = (p: Po) => p.status === 'draft';
export const receivable = (p: Po) => p.status === 'sent' || p.status === 'partially_received';

/**
 * purchaseOrders.cancel takes a draft, a sent PO, or the rest of a part-received one; one being
 * sent waits until it shows as sent, and one cancelled once stays as it is.
 */
export const cancellable = (p: Po) =>
  !p.cancelled && (p.status === 'draft' || p.status === 'sent' || p.status === 'partially_received');

/** What cancelling does, said before the person decides. */
export function cancelOutcome(p: Po, vendor: string): string {
  if (p.status === 'draft') return `${p.number} is cancelled. It was never sent, so ${vendor} isn’t told.`;
  if (p.status === 'partially_received') {
    const n = p.lines.filter((l) => openQty(l) > 0).length;
    return `What’s still expected on ${p.number} is cancelled (${n} ${n === 1 ? 'line' : 'lines'}); what came in stays in stock. ${vendor} is emailed to cancel the rest.`;
  }
  return `${p.number} is cancelled and ${vendor} is emailed that it is. Nothing posts to the books: nothing was received.`;
}

/** The PO sheet's line about a cancelled PO: when, why, and whether the vendor knows. */
export function cancelledNote(p: Po, day: (iso: string) => string): string | null {
  const c = p.cancelled;
  if (!c) return null;
  const what = p.status === 'cancelled' ? 'Cancelled' : 'The rest was cancelled';
  const told = !p.sentAt
    ? ''
    : c.vendorToldAt
      ? ' The vendor was emailed.'
      : c.vendorTellFailure
        ? ` The vendor wasn’t told: ${c.vendorTellFailure}`
        : ' Emailing the vendor…';
  return `${what} ${day(c.at)}: ${c.reason.replace(/[.\s]+$/, '')}.${told}`;
}

/** The one thing to say about a PO's state, or null. */
/** A send that didn't go, on the PO (QA-566): "Not sent: the email to the vendor bounced: call them…". */
export const notSentText = (p: Pick<Po, 'status' | 'sendFailure'>): string | null =>
  p.status === 'draft' && p.sendFailure
    ? `Not sent: ${p.sendFailure.charAt(0).toLowerCase()}${p.sendFailure.slice(1)}`
    : null;

export function poNote(p: Po): string | null {
  if (p.status === 'draft' && p.needsOwnerApproval)
    return 'Over the approval limit: the owner approves it before it can be sent.';
  if (p.status === 'sending') return 'Waiting for the mail provider to confirm it went.';
  const back = p.lines.filter((l) => l.backOrdered).length;
  if (back) return `${back} ${back === 1 ? 'line is' : 'lines are'} back-ordered: still expected.`;
  return null;
}

// ── lines, for create and updateLines ──

export interface LineForm {
  itemId: string;
  name: string;
  quantity: string;
  /** Dollars as typed; blank takes the price book cost. */
  cost: string;
}

/** "12.5" → 1250; null when it isn't a cost. */
export function costCents(text: string): number | null {
  const t = text.replace(/[$,\s]/g, '');
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

export const linesOf = (p: Po): LineForm[] =>
  p.lines.map((l) => ({
    itemId: l.itemId,
    name: l.description,
    quantity: String(Number(l.quantity)),
    cost: (l.unitCostCents / 100).toFixed(2),
  }));

/** What still stops the lines from saving; empty when they can go. */
export function lineProblems(lines: readonly LineForm[]): string[] {
  const out: string[] = [];
  if (!lines.length) out.push('Add at least one item.');
  for (const l of lines) {
    if (!validQty(l.quantity)) out.push(`${l.name}: a quantity like 4 or 2.5.`);
    if (l.cost.trim() && costCents(l.cost) === null)
      out.push(`${l.name}: a cost like 12.50, or leave it blank.`);
  }
  return out;
}

/** The lines as purchaseOrders.create / updateLines take them. */
export const linesInput = (lines: readonly LineForm[]) =>
  lines.map((l) => ({
    itemId: l.itemId,
    quantity: l.quantity.trim(),
    ...(l.cost.trim() ? { unitCostCents: costCents(l.cost)! } : {}),
  }));

// ── receiving ──

export interface ReceiveForm {
  poLineId: string;
  name: string;
  /** Still expected on the line. */
  open: number;
  got: string;
  rest: 'back_order' | 'cancel';
  /** The line's unit and size (truck-stock parity 4): what arrives is counted in it. */
  unit?: string | null;
  per?: string;
}

export const receiveForm = (p: Po): ReceiveForm[] =>
  p.lines
    .filter((l) => openQty(l) > 0)
    .map((l) => ({
      poLineId: l.id,
      name: l.description,
      open: openQty(l),
      got: String(openQty(l)),
      rest: 'back_order',
      // bought by a unit of the vendor's (truck-stock parity 4): what arrives is counted in it
      ...(l.purchaseUnit || Number(l.unitsPerPurchase ?? '1') !== 1
        ? { unit: l.purchaseUnit ?? null, per: l.unitsPerPurchase ?? '1' }
        : {}),
    }));

const RECEIVED = /^\d{1,9}(\.\d{1,3})?$/;

/**
 * What receiving did, in words, from the lines as sent (QA-377): "received in full" only when nothing
 * was cancelled; otherwise what came in, what's still expected and what was cancelled.
 */
export function receivedText(number: string, lines: readonly ReceiveForm[], status: string): string {
  const n = (x: number) => Math.round(x * 1000) / 1000;
  const got = n(lines.reduce((s, l) => s + Number(l.got), 0));
  const rest = (kind: ReceiveForm['rest']) =>
    n(lines.filter((l) => l.rest === kind).reduce((s, l) => s + Math.max(0, l.open - Number(l.got)), 0));
  const cancelled = rest('cancel');
  const expected = rest('back_order');
  if (status === 'received' && !cancelled) return `${number} received in full.`;
  const parts = [
    `${got} in`,
    ...(expected ? [`${expected} still expected`] : []),
    ...(cancelled ? [`${cancelled} cancelled (expect a credit note)`] : []),
  ];
  return status === 'received'
    ? `${number} is closed: ${parts.join(', ')}.`
    : `${number} part received: ${parts.join(', ')}.`;
}

export function receiveProblems(lines: readonly ReceiveForm[]): string[] {
  const out: string[] = [];
  for (const l of lines) {
    if (!RECEIVED.test(l.got.trim())) out.push(`${l.name}: a quantity like 4, or 0 if none came.`);
    else if (Number(l.got) > l.open) out.push(`${l.name}: only ${l.open} still expected.`);
  }
  return out;
}

export const receiveLines = (lines: readonly ReceiveForm[]) =>
  lines.map((l) => ({ poLineId: l.poLineId, quantity: l.got.trim(), rest: l.rest }));

// ── counts (blind: the expected figure is never shown before posting, F-60) ──

export const COUNT_REASONS: { value: string; label: string }[] = [
  { value: 'miscount', label: 'Miscounted before' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'lost', label: 'Lost' },
  { value: 'found', label: 'Found' },
  { value: 'used_not_logged', label: 'Used on a job, not logged' },
  { value: 'other', label: 'Something else' },
];

export interface CountForm {
  itemId: string;
  name: string;
  counted: string;
  reason: string;
  note: string;
}

export function countProblems(lines: readonly CountForm[]): string[] {
  return lines
    .filter((l) => !RECEIVED.test(l.counted.trim()))
    .map((l) => `${l.name}: a count like 4, or 0 if there are none.`);
}

export const countLines = (lines: readonly CountForm[]) =>
  lines.map((l) => ({
    itemId: l.itemId,
    counted: l.counted.trim(),
    ...(l.reason ? { reason: l.reason } : {}),
    ...(l.note.trim() ? { note: l.note.trim() } : {}),
  }));

/** After "Done counting": each line's books figure and the difference, from the levels read. */
export function countDiffs(
  lines: readonly CountForm[],
  onHand: Record<string, string>,
): Record<string, { expected: number; diff: number }> {
  const out: Record<string, { expected: number; diff: number }> = {};
  for (const l of lines) {
    const expected = Number(onHand[l.itemId] ?? '0');
    out[l.itemId] = { expected, diff: Math.round((Number(l.counted) - expected) * 1000) / 1000 };
  }
  return out;
}

/** A line that is off needs its reason before posting (the server's rule, asked for up front). */
export const reasonsMissing = (lines: readonly CountForm[], diffs: ReturnType<typeof countDiffs>) =>
  lines.filter((l) => diffs[l.itemId]?.diff !== 0 && !l.reason).map((l) => l.itemId);

/** The lines the server says need a reason (issues at lines.N.reason), by item. */
export function linesNeedingReason(detail: unknown, lines: readonly CountForm[]): string[] {
  const issues = (detail as { issues?: { path?: (string | number)[] }[] } | null)?.issues ?? [];
  return issues
    .map((i) => i.path)
    .filter((p): p is (string | number)[] => !!p && p[0] === 'lines' && p[2] === 'reason')
    .map((p) => lines[Number(p[1])]?.itemId)
    .filter((x): x is string => !!x);
}

/** "+2", "-1", "0" for a count's difference. */
export function diffText(d: string): string {
  const n = Number(d);
  return n > 0 ? `+${n}` : String(n);
}

// ── an item's min and max ──

export function levelProblem(min: string, max: string): string | null {
  const ok = (s: string) => RECEIVED.test(s.trim());
  if (!ok(min) || !ok(max)) return 'A minimum and maximum like 2 and 6.';
  if (Number(max) < Number(min)) return 'The maximum is below the minimum.';
  return null;
}

/** A job a PO's parts can be for: one still open (purchaseOrders.create refuses a cancelled or completed one). */
export interface OpenJob {
  id: string;
  number: string;
  status: string;
  summary: string;
  customerName: string;
}
export const OPEN_JOB_STATUSES = ['open', 'in_progress', 'awaiting_review'] as const;

/** "24589 · Okafor residence · No hot water", as the job picker lists it. */
export const jobLabel = (j: Pick<OpenJob, 'number' | 'customerName' | 'summary'>) =>
  [j.number, j.customerName, j.summary].filter(Boolean).join(' · ');

/** Whether a PO is ServiceTitan's while the company mirrors it: read here, changed there (BL-088). */
export const stOwned = (p: Pick<Po, 'fromServiceTitan'>, mirroring: boolean): boolean =>
  mirroring && p.fromServiceTitan === true;
